import axios from 'axios';
import uuidv4 from 'uuid/v4';
import defaultParams from './defaultParams';
import { routes } from '../../config/index';

const http = axios.create({
  baseURL: routes.api,
  timeout: 10000,
});
const { CancelToken } = axios;
let source = {};

const cmsUrl = {
  live: 'live_web_iframe_app',
  prematch: 'smweb_iframe_app',
};

export default {
  loadEvent(params, queue) {
    _.assign(params, {
      dataFormat: params.dataFormat || defaultParams.dataFormat,
    });

    if (source.token) source.cancel('New event requested!');
    source = CancelToken.source();

    return http.get(`/api/v1/events/${params.id}`, {
      params,
      ...!queue && { cancelToken: source.token },
    }).then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  payinTicket(payload) {
    const ticketData = {
      ticket: payload.ticket,
      metadata: {
        cpvUuid: payload.config.cpvUuid,
        requestUuid: payload.requestUuid,
        deliveryPlatform: 'Web',
        paymentMethod: payload.settings.user.settings.paymentMethod,
        product: 'SportsbookSM',
        sources: [{
          type: 'player',
          uuid: payload.settings.user.id,
        },
        {
          type: 'productInstance',
          uuid: '3341d17a-44f0-48b2-8b3c-2c5378ba476b',
        }],
      },
    };

    const sevenApi = _.replace(routes.sevenApi, '{dcUrlFragment}', payload.config.dcUrl);

    const options = {
      method: 'POST',
      url: `${sevenApi}/web/tickets/add.json`,
      params: {
        requestUuid: payload.requestUuid,
      },
      headers: {
        Authorization: `Bearer ${payload.settings.user.token}`,
        'SEVEN-TP-TOKEN': payload.settings.auth.tpToken,
        'HTTP-X-SEVEN-CLUB-UUID': payload.settings.company.id,
        'HTTP-X-NAB-DP': 'Web',
      },
      data: ticketData,
    };

    return axios(options)
      .then(response => response)
      .catch(err => Promise.reject(err.toJSON()));
  },
  payinReOfferedTicket(payload) {
    const ticket = {
      reOfferedTicketId: payload.reOfferedTicketId,
      routingKey: {
        value: payload.settings.routingKey,
      },
    };
    return this.payinTicket({
      ticket,
      settings: payload.settings,
      requestUuid: payload.requestUuid,
      reoffer: true,
      config: payload.config,
    }).catch(error => console.error(error));
  },
  rejectReOfferedTicket(reOfferedTicketId, companyUuid) {
    const options = {
      method: 'delete',
      url: `${routes.smGateway}/web/reject-re-offer-tickets`,
      data: { ticket: { id: reOfferedTicketId } },
      headers: {
        'x-nsft-seven-company-uuid': companyUuid,
      },
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  generateWebCode(companyUuid, ticket) {
    const metadata = {
      requestUuid: uuidv4(),
      deliveryPlatform: 'Web',
    };
    const options = {
      method: 'post',
      url: `${routes.smGateway}/generate-web-code`,
      params: {
        companyUuid,
      },
      data: {
        metadata,
        ticket,
      },
    };

    return axios(options)
      .then(() => console.log('Web Code generated!'))
      .catch(err => Promise.reject(err.toJSON()));
  },
  checkCode(code, companyUuid, language) {
    const options = {
      method: 'get',
      url: `${routes.smGateway}/check-code/${code}`,
      params: {
        companyUuid,
        language,
      },
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err));
  },
  getUserFavorites(authToken, playerUuid) {
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    const options = {
      method: 'get',
      url: `${routes.smGateway}/web/favorite-markets/${playerUuid}`,
      headers,
    };

    return axios(options)
      .then(response => response)
      .catch(err => Promise.reject(err.toJSON()));
  },
  updateUserFavorites(authToken, playerUuid, marketData) {
    const { marketId, sportId } = marketData;
    const options = {
      method: 'put',
      url: `${routes.smGateway}/web/favorite-markets/${playerUuid}`,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      data: {
        type: 'object',
        value: { marketId, sportId },
      },
    };
    return axios(options)
      .then(response => response)
      .catch(err => Promise.reject(err.toJSON()));
  },
  getLists({ listName, currentPage, pageSize }, companyUuid, language) {
    return axios({
      url: `${routes.gravityApi}/v1/web/lists/${listName}`,
      method: 'get',
      headers: {
        'X-Nsft-SCD-Company-Id': companyUuid,
        'X-Nsft-SCD-Locale': language,
      },
      params: {
        currentPage: currentPage || 1,
        pageSize: pageSize || 10,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  getTicketHistoryList(playerUuid, companyUuid, filter = {}) {
    return axios({
      url: `${routes.smGateway}/punters-ticket-list/${playerUuid}`,
      method: 'get',
      params: {
        companyUuid,
        'page[limit]': filter.pageLimit || -1,
        'page[offset]': filter.pageOffset || 0,
        'filter[startDate]': filter.startDate || null,
        'filter[endDate]': filter.endDate || null,
        'filter[status]': filter.status || null,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  getCashoutTickets({ companyUuid, token }) {
    return axios({
      url: `${routes.prematchCashout}/client/api/v1/tickets/punter`,
      method: 'get',
      params: {
        companyUuid,
        page: 1,
        size: 200,
        sort: '-placedAt',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  cashoutSMTicket({
    companyUuid,
    playerUuid,
    cpvUuid,
    token,
    requestUuid,
    lang,
    id,
    amount,
    acceptHigher,
    acceptAny,
    dcUrl,
  }) {
    const payload = {
      requestedAmount: amount,
      acceptAnyAmountChange: acceptAny,
      acceptHigherAmountChange: acceptHigher,
      metadata: {
        product: 'SportsbookSM',
        deliveryPlatform: 'Web',
        paymentMethod: 'VirtualMoney',
        requestUuid,
        cpvUuid,
        sources: [
          { type: 'player', uuid: playerUuid },
        ],
      },
      ticket: {
        id,
      },
      tax: [],
    };
    const sevenApi = _.replace(routes.sevenApi, '{dcUrlFragment}', dcUrl);
    return axios({
      url: `${sevenApi}/web/tickets/cashout.json`,
      method: 'put',
      data: payload,
      params: {
        requestUuid,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'HTTP-X-SEVEN-CLUB-UUID': companyUuid,
        'SEVEN-LOCALE': lang,
        'HTTP-X-NAB-DP': 'Web',
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  /**
   *  Global application settings from CMS.
   *  Stored under 'smweb_iframe_app'.
   *
   * @returns {Promise<any>}
   */
  getApplicationSettings(product) {
    const options = {
      method: 'get',
      url: `${routes.gravityApi}/v1/app-templates/${cmsUrl[product]}/settings/config`,
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  /**
   * Company applications settings from CMS.
   *  Stored under ${companyName}_smweb_iframe_app.
   *
   * @param {string} [companyName='']
   * @returns {Promise<any>}
   */
  getCompanyAppSettings(companyName = '', product) {
    const franchises = {
      seven: 'winner',
      magnum: 'winner',
    };
    const reqCompanyName = franchises[companyName] || companyName;
    const options = {
      method: 'get',
      params: {
        templateMerge: true,
      },
      url: `${routes.gravityApi}/v1/apps/${reqCompanyName}_${cmsUrl[product]}/settings`,
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },

  getWebAppConfig(company = {}) {
    const options = {
      method: 'get',
      url: `${routes.smGateway}/web/config`,
      headers: { companyUuid: company.uuid },
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => err);
  },

  getBanners(companyName, lang) {
    const appName = `${companyName}_smweb_iframe_app`;
    const options = {
      method: 'get',
      url: `${routes.gravityApi}/v1/marketing/slots`,
      headers: {
        'X-Nsft-SCD-Locale': lang,
        'X-Nsft-SCD-App-Name': appName,
      },
    };
    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },

  loadLiveTickets({
    cpvUuid,
    timezone,
    language,
    companyUuid,
    token,
    dcUrl,
    historyTicketCount,
  }) {
    const notstatus = 'CLOSED,REJECTED,AFTERCLOSED';
    const product = 'LiveBetting';
    const ticketsApi = _.replace(routes.liveTickets, '{dcUrlFragment}', dcUrl);

    return axios({
      url: `${ticketsApi}/ticketsHistory/${cpvUuid}.json`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
        'http-x-seven-club-uuid': companyUuid,
        'http-x-nab-dp': 'Web',
      },
      params: {
        count: historyTicketCount,
        notstatus,
        product,
        timezone,
        cpvUuid,
        id_language: language,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },

  loadCashoutTickets({
    companyUuid,
    token,
  }) {
    const limit = 50;
    const offset = 0;
    const sort = '-creationDatetime';

    return axios({
      url: `${routes.liveCashout}/v1/tickets`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        limit,
        offset,
        sort,
        companyUuid,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },

  cashoutTicket({
    companyUuid,
    token,
    cashoutSettings,
    ticket,
  }) {
    const { ticketHash } = ticket;
    const currentCashoutAmount = ticket.cashoutAmountAfterTax.toFixed(2);
    const acceptAnyAmountChange = cashoutSettings.options[0].value === cashoutSettings.selected;
    const acceptHigherAmountChange = cashoutSettings.options[1].value === cashoutSettings.selected;

    const requestPath = `v1/cashout/${ticketHash}/${currentCashoutAmount}`;

    return axios({
      url: `${routes.liveCashout}/${requestPath}`,
      method: 'post',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        companyUuid,
        acceptAnyAmountChange,
        acceptHigherAmountChange,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },

  getLiveTicketDetails({
    ticketHash,
    language,
    token,
    dcUrl,
  }) {
    const ticketsApi = _.replace(routes.liveTickets, '{dcUrlFragment}', dcUrl);

    return axios({
      url: `${ticketsApi}/tickets/${ticketHash}.json`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        id_language: language,
      },
    })
      .then(response => response.data);
  },

  getLiveTicketsDetails({
    ticketHashes,
    language,
    companyUuid,
    token,
  }) {
    return axios({
      url: `${routes.liveSocket}/liveTicket.php/tickets`,
      method: 'get',
      headers: {
        'company-uuid': companyUuid,
        Authorization: `Bearer ${token}`,
      },
      params: {
        ticketHashes,
        language,
      },
    })
      .then(response => response.data);
  },

  getStreamUrl({
    cpv,
    language,
    matchId,
    companyUuid,
    token,
  }) {
    const options = {
      url: `${routes.liveSocket}/streaming.php/stream`,
      method: 'get',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        cpv,
        language,
        platform: 'web',
        matchId,
        token: 'web',
        clubUuid: companyUuid,
        bitrate: 1264000,
      },
    };
    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  getLiveConfiguration(cpvUuid, locale) {
    return axios({
      url: `${routes.liveSocket}/clientConfiguration.php/configuration`,
      method: 'get',
      params: {
        cpvUuid,
        locale,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  getBonusConfiguration(cpvUuid, url, companyUuid) {
    return axios({
      url,
      method: 'get',
      params: {
        cpvUuid,
        playerUuid: companyUuid,
      },
    })
      .then(response => response.data)
      .catch(err => Promise.reject(err.toJSON()));
  },
  liveTicketPayin(payload) {
    const sevenApi = _.replace(routes.sevenApi, '{dcUrlFragment}', payload.config.dcUrl);
    const ticketData = {
      ticket: payload.ticket,
      metadata: payload.metadata,
    };
    const options = {
      method: 'POST',
      url: `${sevenApi}/web/tickets/add.json`,
      params: {
        requestUuid: payload.metadata.requestUuid,
      },
      headers: {
        Authorization: `Bearer ${payload.settings.user.token}`,
        'SEVEN-TP-TOKEN': payload.settings.auth.tpToken,
        'HTTP-X-SEVEN-CLUB-UUID': payload.settings.company.id,
        'HTTP-X-NAB-DP': 'Web',
        'SEVEN-LOCALE': payload.settings.language,
      },
      data: ticketData,
    };

    return axios(options)
      .then(response => response)
      .catch(err => Promise.reject(err.response));
  },
  getPendingTicket(payload) {
    const sevenApi = _.replace(routes.sevenApi, '{dcUrlFragment}', payload.config.dcUrl);
    const options = {
      method: 'GET',
      url: `${sevenApi}/web/tickets/request/${payload.requestUuid}/product/LiveBetting/check.json`,
      headers: {
        Authorization: `Bearer ${payload.settings.user.token}`,
        'HTTP-X-SEVEN-CLUB-UUID': payload.settings.company.id,
        'HTTP-X-NAB-DP': 'Web',
        'SEVEN-LOCALE': payload.settings.language,
      },
    };

    return axios(options)
      .then(response => response)
      .catch(err => Promise.reject(err.response));
  },
  generateCode({ ticket, metadata }) {
    const options = {
      url: `${routes.liveSocket}/ticketCode.php/live/ticketCode`,
      method: 'POST',
      data: {
        metadata,
        ticket,
      },
    };
    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.response));
  },
  getGeneratedTicket({ webCode, cpvUuid, locale }) {
    const options = {
      url: `${routes.liveSocket}/ticketCode.php/live/getTicketCode`,
      method: 'get',
      params: {
        webCode,
        cpvUuid,
        locale,
      },
    };

    return axios(options)
      .then(response => response.data)
      .catch(err => Promise.reject(err.response));
  },
  getPlayerSettings(playerUuid, prefix) {
    const options = {
      method: 'get',
      params: {
        prefix,
      },
      url: `${routes.smGateway}/player-settings/${playerUuid}`,
    };

    return axios(options).then(response => response);
  },
  updatePlayerSettings(payload) {
    const options = {
      method: 'put',
      params: {
        prefix: payload.prefix,
      },
      url: `${routes.smGateway}/player-settings/${payload.playerUuid}`,
      data: payload,
    };

    return axios(options)
      .then()
      .catch((err) => { console.log('error updating player settings', err); });
  },
  getLmtBannersCmsUrl(companyName) {
    return `${routes.cms}/${companyName}/live_lmt_banner_images`;
  },
};
